/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import getThumbnailUrls from '../../helpers/getThumbnailUrls';
import { setThumbnailUrls, extendAssets } from '../helpers/assets';
import {
  setListingAssets,
  setListingVariantAssets,
  setMasterAssets,
  setMasterVariantAssets,
} from './helpers/entityAdapter';
import {
  setListingAssetsFetching,
  setListingVariantAssetsFetching,
  setMasterAssetsFetching,
  setMasterVariantAssetsFetching,
} from './helpers/uiAdapter';
import buildProductSelectionSearchBody from './helpers/buildProductSelectionSearchBody';
import {
  BULK_MEDIA_PAGE_SIZE,
  collectSearchTargetsFromSelection,
  splitAssetsByListingProducts,
  splitAssetsByListingVariants,
  splitAssetsByMasterProducts,
  splitAssetsByMasterVariants,
} from './helpers/splitBulkSearchAssets';
import { shouldSuppressMediaErrorToast } from './helpers/showChannelProductGoneToast';

const emptyBulkSelectionAssetsResult = {
  galleryAssets: [],
  total: 0,
  full: true,
  isMoreLoaded: false,
  masterAssetsByProductId: {},
  masterVariantAssetsByVariantId: {},
  listingAssetsByListingId: {},
  listingVariantAssetsByVariantId: {},
  masterProductIds: [],
  masterVariantEntries: [],
  listingProductEntries: [],
  listingVariantEntries: [],
};

const emptyBulkMediaGallery = () => ({
  assets: [],
  total: 0,
  full: false,
  fetching: false,
  loaded: false,
});

const ensureBulkMediaGallery = (state) => {
  if (!state.ui.bulkMediaGallery) {
    state.ui.bulkMediaGallery = emptyBulkMediaGallery();
  }
  return state.ui.bulkMediaGallery;
};

const mergeAssetLists = (current = [], next = []) => {
  if (!next.length) return current;
  if (!current.length) return next;

  const seenIds = new Set(current.map((asset) => String(asset._id)));
  const merged = current.slice();
  next.forEach((asset) => {
    const assetId = String(asset._id);
    if (seenIds.has(assetId)) return;
    seenIds.add(assetId);
    merged.push(asset);
  });
  return merged;
};

const mapAssetsWithPermissions = (assets) => assets.map((asset) => ({
  ...asset,
  permissions: Object
    .keys(asset.permissions || {})
    .reduce((negativePermissions, key) => ({ ...negativePermissions, [key]: false }), {}),
}));

const searchAssetsPage = async (sdk, searchParams) => {
  const { data } = await sdk.assets.search(searchParams);
  return {
    images: data.images?.length ? extendAssets(data.images) : [],
    total: data.total || 0,
    full: Boolean(data.full),
  };
};

const applyBulkSelectionAssetsToState = (state, {
  masterAssetsByProductId,
  masterVariantAssetsByVariantId,
  listingAssetsByListingId,
  listingVariantAssetsByVariantId,
  masterProductIds,
  masterVariantEntries,
  listingProductEntries,
  listingVariantEntries,
}) => {
  masterProductIds.forEach((productId) => {
    const nextAssets = masterAssetsByProductId[productId] || [];
    if (!nextAssets.length && state.entities.assets.masterAssets?.[productId]) return;

    const assets = mergeAssetLists(
      state.entities.assets.masterAssets?.[productId],
      nextAssets,
    );
    state.items.forEach((product) => {
      if (product._id === productId) {
        product.fetchingAssets = false;
        product.assets = assets;
      }
    });
    setMasterAssets(state, productId, assets);
    setMasterAssetsFetching(state, productId, false);
  });

  masterVariantEntries.forEach(({ variantId, productId }) => {
    const nextAssets = masterVariantAssetsByVariantId[variantId] || [];
    if (!nextAssets.length && state.entities.assets.masterVariantAssets?.[variantId]) return;

    const assets = mergeAssetLists(
      state.entities.assets.masterVariantAssets?.[variantId],
      nextAssets,
    );
    state.items.forEach((product) => {
      if (product._id !== productId) return;

      if (!product.variantAssetsById) product.variantAssetsById = {};
      product.variantAssetsById[variantId] = assets;
      if (product.fetchingVariantAssetsById) {
        delete product.fetchingVariantAssetsById[variantId];
      }
    });
    setMasterVariantAssets(state, variantId, assets);
    setMasterVariantAssetsFetching(state, variantId, false);
  });

  listingProductEntries.forEach(({ listingProductId }) => {
    const nextAssets = listingAssetsByListingId[listingProductId] || [];
    if (!nextAssets.length && state.entities.assets.listingAssets?.[listingProductId]) return;

    setListingAssets(
      state,
      listingProductId,
      mergeAssetLists(state.entities.assets.listingAssets?.[listingProductId], nextAssets),
    );
    setListingAssetsFetching(state, listingProductId, false);
  });

  listingVariantEntries.forEach(({ variantId }) => {
    const nextAssets = listingVariantAssetsByVariantId[variantId] || [];
    if (!nextAssets.length && state.entities.assets.listingVariantAssets?.[variantId]) return;

    setListingVariantAssets(
      state,
      variantId,
      mergeAssetLists(state.entities.assets.listingVariantAssets?.[variantId], nextAssets),
    );
    setListingVariantAssetsFetching(state, variantId, false);
  });
};

export const fetchBulkSelectionAssets = createAsyncThunk(
  'products/fetchBulkSelectionAssets',
  async (arg, {
    extra: { Logger, Toast, sdk },
    getState,
  }) => {
    const isMoreLoaded = Boolean(arg?.isMoreLoaded);
    const productsState = getState().products;
    const selection = productsState.ui.selection;
    const {
      masterProductIds,
      masterVariantEntries,
      listingProductEntries,
      listingVariantEntries,
    } = collectSearchTargetsFromSelection(selection);

    if (
      !masterProductIds.length
      && !masterVariantEntries.length
      && !listingProductEntries.length
      && !listingVariantEntries.length
    ) {
      return emptyBulkSelectionAssetsResult;
    }

    try {
      const { team } = getState().user;
      const rootCollectionId = getState().collections?.collections?.my?._id;
      const gallery = productsState.ui.bulkMediaGallery || emptyBulkMediaGallery();
      const from = isMoreLoaded ? gallery.assets.length : 0;
      const listingProductIds = listingProductEntries.map(({ listingProductId }) => listingProductId);
      const productIds = [...masterProductIds, ...listingProductIds];
      const variantIds = [
        ...masterVariantEntries.map(({ variantId }) => variantId),
        ...listingVariantEntries.map(({ variantId }) => variantId),
      ];
      const scope = buildProductSelectionSearchBody(selection);
      const productSelection = scope && (productIds.length || variantIds.length)
        ? {
          layer: scope.layer,
          ...(scope.recordState ? { recordState: scope.recordState } : {}),
          ...(productIds.length ? { productIds } : {}),
          ...(variantIds.length ? { variantIds } : {}),
        }
        : null;

      const page = productSelection
        ? await searchAssetsPage(sdk, {
          tagId: rootCollectionId,
          from,
          size: BULK_MEDIA_PAGE_SIZE,
          productSelection,
        })
        : { images: [], total: 0, full: true };

      const assetsById = new Map();
      page.images.forEach((asset) => {
        assetsById.set(String(asset._id), asset);
      });

      const applyThumbnailsAndPermissions = (assets) => {
        if (!assets.length) return [];

        const assetsWithThumbs = setThumbnailUrls(
          assets,
          thumbnailUrls,
          team?.storageType === 's3',
        );
        return mapAssetsWithPermissions(assetsWithThumbs);
      };

      const thumbnailUrls = assetsById.size
        ? await getThumbnailUrls([...assetsById.keys()])
        : {};

      const processedAssets = applyThumbnailsAndPermissions(page.images);
      const reachedEnd = page.full
        || processedAssets.length < BULK_MEDIA_PAGE_SIZE
        || from + processedAssets.length >= page.total;

      return {
        galleryAssets: processedAssets,
        total: page.total,
        full: reachedEnd,
        isMoreLoaded,
        masterAssetsByProductId: splitAssetsByMasterProducts(
          processedAssets,
          masterProductIds,
        ),
        masterVariantAssetsByVariantId: splitAssetsByMasterVariants(
          processedAssets,
          masterVariantEntries,
        ),
        listingAssetsByListingId: splitAssetsByListingProducts(
          processedAssets,
          listingProductEntries,
        ),
        listingVariantAssetsByVariantId: splitAssetsByListingVariants(
          processedAssets,
          listingVariantEntries,
        ),
        masterProductIds,
        masterVariantEntries,
        listingProductEntries,
        listingVariantEntries,
      };
    } catch (error) {
      Logger.log('UI', 'ToastProductsNotLoaded');
      if (!shouldSuppressMediaErrorToast()) {
        Toast(
          'Selection media not loaded. Refresh and try again',
          { autoClose: false, type: 'error' },
        );
      }
      Logger.error(new Error('Error bulk selection assets fetching'), { error }, [
        'FetchBulkSelectionAssetsFailed',
        { errorMessage: 'Bulk selection assets can not be fetched from backend.' },
      ]);
      throw error;
    }
  },
  {
    condition: (arg, { getState }) => {
      const { selection, bulkMediaGallery } = getState().products.ui;
      if (!buildProductSelectionSearchBody(selection)) return false;
      if (bulkMediaGallery?.fetching) return false;
      if (arg?.isMoreLoaded) {
        return Boolean(bulkMediaGallery?.loaded) && !bulkMediaGallery.full;
      }
      return true;
    },
  },
);

export const reducer = (builder) => {
  builder.addCase(
    fetchBulkSelectionAssets.pending,
    (state, action) => {
      const gallery = ensureBulkMediaGallery(state);
      gallery.fetching = true;
      if (!action.meta.arg?.isMoreLoaded) {
        gallery.assets = [];
        gallery.total = 0;
        gallery.full = false;
        gallery.loaded = false;
      }
    },
  ).addCase(
    fetchBulkSelectionAssets.fulfilled,
    (state, { payload }) => {
      const gallery = ensureBulkMediaGallery(state);
      gallery.fetching = false;
      gallery.loaded = true;
      gallery.total = payload.total || 0;
      gallery.full = Boolean(payload.full);
      gallery.assets = payload.isMoreLoaded
        ? mergeAssetLists(gallery.assets, payload.galleryAssets || [])
        : (payload.galleryAssets || []);

      applyBulkSelectionAssetsToState(state, {
        masterAssetsByProductId: payload.masterAssetsByProductId || {},
        masterVariantAssetsByVariantId: payload.masterVariantAssetsByVariantId || {},
        listingAssetsByListingId: payload.listingAssetsByListingId || {},
        listingVariantAssetsByVariantId: payload.listingVariantAssetsByVariantId || {},
        masterProductIds: payload.masterProductIds || [],
        masterVariantEntries: payload.masterVariantEntries || [],
        listingProductEntries: payload.listingProductEntries || [],
        listingVariantEntries: payload.listingVariantEntries || [],
      });
    },
  ).addCase(
    fetchBulkSelectionAssets.rejected,
    (state, action) => {
      const gallery = ensureBulkMediaGallery(state);
      gallery.fetching = false;
      if (!action.meta.arg?.isMoreLoaded) {
        gallery.loaded = true;
        gallery.full = true;
        gallery.assets = [];
        gallery.total = 0;
      }
      state.error = action.error;
    },
  );
};
